import { action, observable, reaction, makeObservable } from 'mobx'
import {
  getErrorMessageFromApiResponse,
  setPointerErrorsFromApiResponse,
} from '../utils/api/apiUtils'
import apiClient from '../utils/api/apiClient'
import { APIEndpoints } from '../utils/api/apiConfig'
import axios from 'axios'
import { emptyGuid, isEmptyGuid } from '../utils/emptyGuid'
import { getEmployee } from '../utils/employeeLoader'
import i18n from '../translation/i18n'
import { isEmptyStr } from '../utils/objectutils'

const CancelToken = axios.CancelToken
var source
class InventoryStore {
  constructor(appStore) {
    makeObservable(this, {
      contingents: observable,
      loadContingentIsLoading: observable,
      loadContingentError: observable,
      searchText: observable,
      filteredContingents: observable,
      employeeId: observable,
      targetEmployeeId: observable,
      employeeData: observable,
      isAssetObject: observable,
      articlesExpanded: observable,
      showAllArticles: observable,
      articleDetails: observable,
      articleDetailsIsLoading: observable,
      articleHistories: observable,
      articleHistoriesIsLoading: observable,
      articleInstances: observable,
      articleInstancesIsLoading: observable,
      selectedArticles: observable,
      scrollRef: observable,
      selectedReturnAmounts: observable,
      actionType: observable,
      articleVariants: observable,
      submitSuccess: observable,
      submitError: observable,
      submitErrorPointers: observable,
      loadingSave: observable,
      loadInstancesLoading: observable,
      instances: observable,
      showContactDataModal: observable,
      catalogArticles: observable,
      editCatalogArticles: observable,
      catalogArticlesTotalCount: observable,
      catalogArticlesOffset: observable,
      catalogArticlesLoading: observable,
      getCatalogArticles: action,
      getCurrentArticle: action,
      loadEmployeeContingent: action,
      loadArticleVariants: action,
      availableVariantOptions: action,
      addCorrectionVariant: action,
      deleteCorrectionVariant: action,
      onMoreOptionsHandler: action,
      initAssetInventory: action,
      loadContingentArticleDetails: action,
      loadContingentArticleHistories: action,
      loadContingentArticleInstances: action,
      saveArticleComment: action,
      saveRFIDisNeutralizedForEmployee: action,
      expandArticles: action,
      setScrollRef: action,
      missingImagesList: observable,
      retourResponse: observable,
      reorderResponse: observable,
      openDeactivateModal: observable,
      setOpenDeactivateModal: action,
      setReturnResponse: action,
      setReorderResponse: action,
      navigateToBasket: action,
      onSubmit: action,
      createParams: action,
      instanceAlreadyExists: action,
      getArticlesByArticleId: action,
      loadAllInstances: action,
      returnReasons: observable,
      reorderForAssetsIsEnabled: observable,
      reorderMissingAmounts: observable,
      automaticReturnAmounts: observable,
      setReorderForAssetsIsEnabled: action,
      setReorderMissingAmounts: action,
      setIsAssetObject: action,
      jumpToOrderAssets: observable,
      setJumpToOrderAssets: action,
      scanModus: observable,
      setScanModus: action,
      openScanModal: observable,
      setOpenScanModal: action,
      setActionType: action,
      setEmployeeId: action,
      setLoadingSave: action,
      setSubmitSuccess: action,
      setSubmitError: action,
      setSubmitErrorPointers: action,
      setEditCatalogArticles: action,
      setContingents: action,
      setShowAllArticles: action,
      setSearchText: action,
      deleteRow: action,
      updateRows: action,
      deleteVariant: action,
      updateInstances: action,
      updateComment: action,
      updateReason: action,
      clearFilterArticles: action,
      allowMaintenanceReport: observable,
      setAllowMaintenanceReport: action,
      updateArticleInstanceItem: action,
      neutralizedRFID: action,
      setNeutralizedRFIDs: action,
      neutralizedRFIDs: observable,
      createRFID: action,
      applyReasonToAllRows: action,
      checkInventoryExistsAskForDeactivation: action,
    })
    this.app = appStore
    reaction(
      () => this.searchText,
      () => {
        this.filterArticles()
      }
    )
    reaction(
      () => this.contingents,
      () => {
        this.filterArticles()
      }
    )
  }
  neutralizedRFIDs = []
  allowMaintenanceReport = false
  scanModus = false
  openScanModal = false
  employeeData = null
  reorderForAssetsIsEnabled = false
  reorderMissingAmounts = false
  automaticReturnAmounts = false
  contingents = null
  loadContingentIsLoading = false
  loadContingentError = null
  searchText = ''
  filteredContingents = []
  employeeId = ''
  targetEmployeeId = ''
  articlesExpanded = []
  showAllArticles = false
  articleDetails = null
  articleDetailsIsLoading = false
  articleHistories = null
  articleHistoriesIsLoading = false
  articleInstances = []
  articleInstancesIsLoading = false
  selectedArticles = null
  scrollRef = null
  selectedReturnAmounts = []
  actionType = ''
  articleVariants = null
  submitSuccess = ''
  submitError = ''
  submitErrorPointers = {}
  loadingSave = false
  loadInstancesLoading = false
  instances = []
  showContactDataModal = false

  catalogArticles = []
  editCatalogArticles = []
  catalogArticlesTotalCount = 0
  catalogArticlesOffset = 0
  catalogArticlesLoading = false
  isAssetObject = false

  async getCatalogArticles(
    newParams = {
      newOffset: this.catalogArticlesOffset,
      newSearch: this.searchText,
    }
  ) {
    if (source) {
      //cancel previous calls that are still pending
      source.cancel('canceled')
    }
    //create new token source
    source = CancelToken.source()
    this.catalogArticlesLoading = true

    let append = !!newParams.newOffset

    const params = this.getParamsForArticlesRequest(newParams)
    try {
      let response = await apiClient.getJson(
        APIEndpoints.articles,
        params,
        true,
        true,
        true,
        { cancelToken: source.token }
      )
      let data = response.data.map((article) => ({ ...article, positions: [] }))
      this.catalogArticles = append ? [...this.catalogArticles, ...data] : data
      this.catalogArticlesTotalCount = response.headers['x-total-count']
        ? parseInt(response.headers['x-total-count'])
        : 0
      this.catalogArticlesOffset = newParams.newOffset + 10
    } catch (e) {
    } finally {
      this.catalogArticlesLoading = false
    }
  }
  getParamsForArticlesRequest(newParams) {
    const { newOffset, newSearch } = newParams
    return {
      'filter.searchText': newSearch ? newSearch : null,
      'page.offset': newOffset,
      'page.limit': 10,
      'filter.targetBusinesspartnerId': this.targetEmployeeId
        ? this.targetEmployeeId
        : this.employeeId,
    }
  }

  // gets the current article from the contingents object (cloned) that we want to edit
  getCurrentArticle(article) {
    const contingentId = article.contingentAvailability.id
    const categoryId = article.categoryAvailability.id
    const articleId = article.article.id
    let contingent = this.contingents.find(
      (contingent) => contingent.id === contingentId
    )
    let category = null
    let foundArticle = null

    if (this.loadContingentIsLoading) {
      //wait until loading contingent is completed
      return this.getCurrentArticle(article)
    }
    if (contingent) {
      category = contingent.categories.find(
        (category) => category.id === categoryId
      )
      if (category) {
        foundArticle = category.articles.find(
          (article) => article.article.id === articleId
        )
      }
    }
    if (foundArticle) {
      return foundArticle
    }

    if (contingentId === emptyGuid) {
      //is catalog article
      foundArticle = this.editCatalogArticles.find(
        (article) => article.article.id === articleId
      )
      if (foundArticle) {
        return foundArticle
      } else {
        this.editCatalogArticles.push(article)
        return this.getCurrentArticle(article)
      }
    }
    return null
  }
  async loadEmployeeContingent(force) {
    if (!force) {
      if (this.contingents) {
        return this.contingents
      }
    }
    if (this.loadContingentIsLoading) {
      await this.app.uiStore.waitUntilLoaded(
        100,
        'app.inventoryStore.loadContingentIsLoading'
      )
      return this.contingents
    } else {
      this.setSearchText('')
      this.setContingents(null)
      this.setEditCatalogArticles([])
      this.loadContingentError = null
      this.loadContingentIsLoading = true
      try {
        let resp = await apiClient.getJson(
          APIEndpoints.businessPartner(this.employeeId).contingent
        )
        this.setContingents(resp)
      } catch (e) {
        this.loadContingentError = getErrorMessageFromApiResponse(e)
      }
      this.loadContingentIsLoading = false
      return this.contingents
    }
  }

  async loadArticleVariants(article) {
    if (article.variants) {
      return
    }
    const variants = await apiClient.getJson(
      APIEndpoints.articleVariants(article.article.id)
    )

    let currentArticle = this.getCurrentArticle(article)
    currentArticle.variants = variants
  }

  availableVariantOptions(articleVariants, articlePositions) {
    let availableOptions = []
    //singleItem with no currentQuantity
    if (
      articleVariants &&
      !articleVariants.length &&
      !articlePositions.length
    ) {
      availableOptions.push({
        id: null,
        displayName: null,
      })
      return availableOptions
    }
    if (articleVariants) {
      articleVariants.forEach((variant) => {
        let foundVariant = articlePositions.find(
          (position) => position.articleVariant.id === variant.id
        )
        if (!foundVariant) {
          availableOptions.push({
            id: variant.id,
            displayName: variant.displayName,
          })
        }
      })
    }
    return availableOptions
  }

  getPosition(article, positionVariant) {
    let foundPosition =
      article.positions.find(
        (position) =>
          (position.articleVariant?.id ?? emptyGuid) ===
          (positionVariant?.id ?? emptyGuid)
      ) || article.positions[0]
    return foundPosition
  }
  //position.negativeAmount = false
  //position.rows = {instance:null,comment:''}
  rowsTemplate = { instance: null, comment: '' }
  updateRows(article, positionVariant, newAmount, negativeAmount = false) {
    //find article that has to be modified
    let updatedArticle = this.getCurrentArticle(article)

    //const foundPosition = updatedArticle.positions.find(position => position.articleVariant && position.articleVariant.id === positionVariant.id) || updatedArticle.positions[0]
    let foundPosition = this.getPosition(updatedArticle, positionVariant)
    if (!foundPosition.rows) {
      foundPosition.rows = []
    }
    let oldAmount = foundPosition.rows.length

    if (newAmount < oldAmount) {
      foundPosition.rows.splice(newAmount)
    } else if (newAmount > oldAmount) {
      let newRows = Array.from({ length: newAmount - oldAmount }, () => ({ instance: null, comment: '' }))
      foundPosition.rows.push(...newRows)
    }

    foundPosition.zeroAmountIsConfirmed = newAmount === 0 ///=> necessary for actionType === 'assets' => confirm zero inventory

    foundPosition.negativeAmount = negativeAmount
  }

  deleteRow(article, positionVariant, index) {
    //find article that has to be modified
    let updatedArticle = this.getCurrentArticle(article)
    //const foundPosition = updatedArticle.positions.find(position => position.articleVariant && position.articleVariant.id === positionVariant.id) || updatedArticle.positions[0]
    let foundPosition = this.getPosition(updatedArticle, positionVariant)
    if (this.actionType === 'assets') {
      //neutralized RFIDs
      let instance = foundPosition.rows[index].instance
      if (instance) {
        this.neutralizedRFID(instance)
      }
    }
    foundPosition.rows.splice(index, 1)
  }
  deleteVariant(article, positionVariant) {
    //find article that has to be modified
    let updatedArticle = this.getCurrentArticle(article)
    let foundPosition = this.getPosition(updatedArticle, positionVariant)
    foundPosition.rows = null
  }
  updateInstances(article, positionVariant, newInstance, index) {
    let updatedArticle = this.getCurrentArticle(article)
    let foundPosition = this.getPosition(updatedArticle, positionVariant)
    foundPosition.rows[index].instance = newInstance
  }
  createRFID(article, positionVariant, newRFID, index) {
    let updatedArticle = this.getCurrentArticle(article)
    let foundPosition = this.getPosition(updatedArticle, positionVariant)
    foundPosition.rows[index].instance = {
      serialKey: '',
      batchKey: '',
      rfid: newRFID,
    }
  }
  updateComment(article, positionVariant, newValue, index) {
    let updatedArticle = this.getCurrentArticle(article)
    let foundPosition = this.getPosition(updatedArticle, positionVariant)
    foundPosition.rows[index].comment = newValue
  }
  updateReason(article, positionVariant, newValue, index) {
    let updatedArticle = this.getCurrentArticle(article)
    let foundPosition = this.getPosition(updatedArticle, positionVariant)
    foundPosition.rows[index].changeReasonId = newValue
  }
  // NEW: bulk apply reason to all rows (used for inventoryBookings + returnBookings)
  applyReasonToAllRows = (changeReasonId, { overwrite = false } = {}) => {
    if (!changeReasonId) return 0

    const applyToRows = (rows) => {
      if (!rows || !rows.length) return 0

      let applied = 0

      rows.forEach((row) => {
        const hasReason = !!row.changeReasonId
        if (!overwrite && hasReason) return

        row.changeReasonId = changeReasonId
        applied += 1
      })

      return applied
    }

    let totalApplied = 0

    // Contingent articles
    this.contingents?.forEach((contingent) => {
      contingent.categories?.forEach((category) => {
        category.articles?.forEach((article) => {
          article.positions?.forEach((position) => {
            totalApplied += applyToRows(position.rows)
          })
        })
      })
    })

    // Catalog articles (added articles)
    this.editCatalogArticles?.forEach((article) => {
      article.positions?.forEach((position) => {
        totalApplied += applyToRows(position.rows)
      })
    })

    return totalApplied
  }

  addCorrectionVariant(article, selectedOption) {
    let newVariant = {
      articleVariant: selectedOption.id
        ? {
            displayName: selectedOption.displayName,
            separatedDisplayName: null,
            groupDisplayName: null,
            isObsolete: null,
            isChangeable: false,
            id: selectedOption.id,
          }
        : null,
      currentQuantity: 0,
      orderedQuantity: 0,
      returnableQuantity: 0,
      shippedQuantity: 0,
      isUserAdded: true,
    }
    let currentArticle = this.getCurrentArticle(article)
    currentArticle.positions.push(newVariant)
  }
  deleteCorrectionVariant(article, positionIndex) {
    let currentArticle = this.getCurrentArticle(article)
    currentArticle.positions.splice(positionIndex, 1)
  }
  async checkScanModus() {
    if (this.actionType !== 'assets' || !this.contingents) return
    this.resetAssetInventory()
  }
  async resetAssetInventory() {
    if (this.loadContingentIsLoading) {
      await this.app.uiStore.waitUntilLoaded(
        100,
        'app.inventoryStore.loadContingentIsLoading'
      )
      return this.resetAssetInventory()
    }
    this.setJumpToOrderAssets(false)
    this.setReorderMissingAmounts(false)
    this.setAutomaticReturnAmounts(false)
    this.clearFilterArticles()
    for (let contingent of this.contingents) {
      this.onMoreOptionsHandler(
        contingent,
        this.scanModus ? 'fillScanMode' : 'fill'
      )
    }
  }
  async initAssetInventory() {
    if (this.actionType !== 'assets') return

    this.resetAssetInventory()
    const info = await apiClient.getJson(
      APIEndpoints.assets(this.employeeId).info
    )
    this.setIsAssetObject(this.info?.isAsset ?? false)
    this.setReorderForAssetsIsEnabled(info?.type?.enableOrderProcess ?? false)
  }
  setIsAssetObject(bool) {
    this.isAssetObject = bool
  }
  setReorderForAssetsIsEnabled(bool) {
    this.reorderForAssetsIsEnabled = bool
  }
  setReorderMissingAmounts(bool) {
    this.reorderMissingAmounts = bool
  }
  setAutomaticReturnAmounts(bool) {
    this.automaticReturnAmounts = bool
  }
  onConfirmCompleteScan() {
    this.clearFilterArticles()
    for (let contingent of this.contingents) {
      this.onMoreOptionsHandler(contingent, 'confirmedScanMode')
    }
    this.setScanModus(false)
  }
  onMoreOptionsHandler(item, action) {
    const fillOrClearArticle = (article) => {
      let currentArticle = this.getCurrentArticle(article)
      if (currentArticle) {
        currentArticle.positions.forEach((position) => {
          if (action === 'confirmedScanMode') {
            position.confirmed = true
            if (position.rows && position.rows.length) {
              return
            }
            position.rows = []
          } else if (action === 'fillScanMode') {
            if (position.confirmed && position.rows && position.rows.length) {
              return
            }
            position.confirmed = false
            position.rows = null
          } else if (!position.confirmed) {
            position.rows = null
            if (action === 'fill') {
              let posQuantity =
                this.actionType !== 'assets' ? position.returnableQuantity : 0 //position.currentQuantity => confirm zero inventory
              position.rows = Array.from({ length: posQuantity }, () => ({ instance: null, comment: '' }))
            }
          }
        })
      }
    }
    // fillOrClearArticle for a contingent
    if (item.categories && item.categories.length) {
      this.filteredContingents
        .find((contingent) => contingent.id === item.id)
        .categories.forEach((category) => {
          category.articles.forEach((article) => {
            fillOrClearArticle(article)
          })
        })
    }
    // fillOrClearArticle for a category
    else if (item.articles && item.articles.length) {
      this.filteredContingents
        .find(
          (contingent) =>
            contingent.categories &&
            contingent.categories.find((category) => category.id === item.id)
        )
        .categories.find((category) => category.id === item.id)
        .articles.forEach((article) => {
          fillOrClearArticle(article)
        })
    }
    // fillOrClearArticle for a single article
    else {
      fillOrClearArticle(item)
    }
  }

  async loadContingentArticleDetails() {
    this.articleDetailsIsLoading = true
    this.articleDetails = await apiClient.getJson(
      APIEndpoints.businessPartner(this.employeeId, this.contingentArticleId)
        .articleDetails
    )
    this.articleDetailsIsLoading = false
  }

  async loadContingentArticleHistories() {
    this.articleHistoriesIsLoading = true
    this.articleHistories = await apiClient.getJson(
      APIEndpoints.businessPartner(this.employeeId, this.contingentArticleId)
        .articleHistories
    )
    this.articleHistoriesIsLoading = false
  }
  async loadContingentArticleInstances() {
    this.articleInstancesIsLoading = true
    this.articleInstances = await apiClient.getJson(
      APIEndpoints.businessPartner(this.employeeId, this.contingentArticleId)
        .articleInstances
    )
    this.articleInstancesIsLoading = false
  }
  updateArticleInstanceItem(instanceId, name, value) {
    let foundIndex = this.articleInstances.findIndex(
      (item) => item.instanceId === instanceId
    )
    if (foundIndex >= 0) {
      this.articleInstances[foundIndex][name] = value
    }
  }
  setNeutralizedRFIDs(value) {
    this.neutralizedRFIDs = value
  }
  neutralizedRFID(instance) {
    if (!instance.rfid) {
      return
    }
    let foundIndex = this.instances.findIndex(
      (item) =>
        item.instanceId === instance.instanceId ||
        item.trackingId === instance.trackingId
    )
    if (foundIndex >= 0) {
      let rfidToNeutralize = instance.rfid
      if (instance.batchKey || instance.serialKey) {
        this.instances[foundIndex].rfid = null
      } else {
        this.instances.splice(foundIndex, 1)
      }
      let rfids = [...this.neutralizedRFIDs]
      rfids.push(rfidToNeutralize)
      this.setNeutralizedRFIDs(rfids)
    }
  }
  async saveArticleComment(comment) {
    return await apiClient.putJson(
      APIEndpoints.businessPartner(this.employeeId, this.contingentArticleId)
        .articleComment,
      comment
    )
  }
  async saveRFIDisNeutralizedForEmployee(rfid) {
    await apiClient.patchJson(
      APIEndpoints.bussinesspartnerRFIDneutralization(
        this.employeeId,
        this.contingentArticleId,
        rfid
      )
    )
  }
  async setAllowMaintenanceReport() {
    this.allowMaintenanceReport = await apiClient.getJson(
      APIEndpoints.layoutTypeIsAvailable('maintenance')
    )
  }
  async setEmployeeId(employeeId) {
    if (employeeId) {
      this.employeeId = employeeId
    } else {
      const profile = await this.app.profileStore.loadProfile()
      this.employeeId = profile.id
    }
    await this.loadEmployeeData(this.employeeData?.id !== this.employeeId)
  }
  async setSelectedContingentArticleId(contingentArticleId) {
    this.contingentArticleId = contingentArticleId
    return this.contingentArticleId
  }
  async loadEmployeeData(force) {
    this.employeeData = await getEmployee(this.employeeId, force)
    this.setIsAssetObject(this.employeeData?.isAsset ?? false)
    return this.employeeData
  }
  setTargetEmployeeId(targetId) {
    if (targetId) {
      this.targetEmployeeId = targetId
    } else this.targetEmployeeId = ''
  }
  setActionType(actionType) {
    this.actionType = actionType
  }
  setSubmitSuccess(bool) {
    this.submitSuccess = bool
  }
  setSubmitError(message) {
    this.submitError = message
  }
  setSubmitErrorPointers(value) {
    this.submitErrorPointers = value
  }
  setLoadingSave(bool) {
    this.loadingSave = bool
  }
  setContingents(value) {
    this.contingents = value
  }
  setEditCatalogArticles(value) {
    this.editCatalogArticles = value
  }
  setShowAllArticles(bool) {
    this.showAllArticles = bool
  }
  setSearchText(value) {
    this.searchText = value
  }
  clearFilterArticles() {
    if (!isEmptyStr(this.searchText)) {
      this.setSearchText('')
    }
  }
  filterArticles() {
    const searchText = this.searchText.toLowerCase()

    if (this.contingents === null) {
      this.filteredContingents = []
      return
    }

    const clonedContingents = JSON.parse(JSON.stringify(this.contingents))
    this.filteredContingents = clonedContingents
      .map((contingent) => {
        contingent.categories = contingent.categories
          .map((category) => {
            category.articles = category.articles.filter((article) => {
              let filterSearchText =
                article.article.displayName
                  .toLowerCase()
                  .indexOf(searchText) !== -1 ||
                article.article.articleNumber
                  .toLowerCase()
                  .indexOf(searchText) !== -1
              if (this.actionType === 'assets') {
                return filterSearchText
              }
              return (
                filterSearchText &&
                article.articleAvailability &&
                article.articleAvailability.currentAmount &&
                article.articleAvailability.currentAmount > 0
              )
            })
            return category
          })
          .filter((category) => category.articles.length > 0)
        return contingent
      })
      .filter((contingent) => contingent.categories.length > 0)

    if (this.showAllArticles) {
      this.getCatalogArticles({ newOffset: 0, newSearch: this.searchText })
    }
  }

  expandArticles = (articleId) => {
    if (this.articlesExpanded.includes(articleId)) {
      this.articlesExpanded.splice(this.articlesExpanded.indexOf(articleId), 1)
    } else {
      this.articlesExpanded.push(articleId)
    }
  }

  setScrollRef(id) {
    this.scrollRef = id
  }
  missingImagesList = []

  reorderResponse = {}
  retourResponse = {}
  openDeactivateModal = false
  jumpToOrderAssets = false

  async checkInventoryExistsAskForDeactivation() {
    const bpHasInventory = await apiClient.getJson(
        APIEndpoints.businessPartner(this.employeeId).checkInventory
      )
    if(!bpHasInventory)
    {
      this.setOpenDeactivateModal(true)
    }
  }
  setOpenDeactivateModal(bool) {
    this.openDeactivateModal = bool
  }
  setReorderResponse(response = {}) {
    this.reorderResponse = response
  }
  setReturnResponse(response = {}) {
    this.retourResponse = response
  }
  setJumpToOrderAssets(bool) {
    this.jumpToOrderAssets = bool
  }
  setScanModus(bool) {
    this.scanModus = bool
    this.setOpenScanModal(bool)
  }
  setOpenScanModal(bool) {
    this.openScanModal = bool
  }
  navigateToBasket(navigate) {
    if (!isEmptyGuid(this.reorderResponse?.id)) {
      this.app.productsStore.loadShoppingBasket(this.reorderResponse.id, true)
      this.app.uiStore.fetchNewShoppingCarts()
      let path = `/order/asset/shoppingcart/${this.reorderResponse.id}`
      navigate(path)
    } else if (this.jumpToOrderAssets) {
      let path = `/products/asset/${this.employeeId}`
      navigate(path)
    }
  }
  async onSubmit(navigate, currentLocation) {
    const params = this.createParams()

    this.setSubmitError('')
    this.setSubmitErrorPointers({})
    this.setSubmitSuccess(false)
    this.setLoadingSave(true)
    let allowDeactivateEmployee = this.app.uiStore.allowChangeActivationStatus
    try {
      const result = await apiClient.postJson(
        APIEndpoints.businessPartner(this.employeeId)[this.actionType],
        params
      )
      const deactivateEmployee =
        allowDeactivateEmployee &&
        (this.actionType === 'returnBookings' ||
          this.actionType === 'articleTransfer' ||
          this.actionType === 'assetArticleTransfer')

      //reset
      this.loadEmployeeContingent(true)
      this.setEditCatalogArticles([])
      this.setNeutralizedRFIDs([])
      if (this.actionType === 'assets') {
        this.setReorderResponse(result?.basket)
        this.setReturnResponse(result?.return)
        if (
          isEmptyGuid(result?.return?.id) &&
          (!isEmptyGuid(result?.basket?.id) || this.jumpToOrderAssets)
        ) {
          this.navigateToBasket(navigate)
        } else {
          this.resetAssetInventory()
        }
      } else if (
        this.actionType === 'inventoryBookings' &&
        result &&
        result.length &&
        result.some((e) => e.changeReason?.isPictureRequired)
      ) {
        this.missingImagesList = result.filter(
          (e) => e.changeReason?.isPictureRequired
        )
        let path = '/inventoryBookings/imageUpload'
        navigate(path)
      } else if (
        this.actionType === 'returnBookings' &&
        this.app.uiStore.allowBookingEmployeeReturnsWithoutInventoryBookings &&
        result.id
      ) {
        this.setReturnResponse(result)
      } else if (deactivateEmployee) {
        this.checkInventoryExistsAskForDeactivation()
      } else {
        this.setSubmitSuccess(true)
        let path = currentLocation.state.from
        setTimeout(() => {
          navigate(path)
        }, 2000)
      }
    } catch (err) {
      this.setSubmitError(getErrorMessageFromApiResponse(err))
      this.setSubmitErrorPointers(setPointerErrorsFromApiResponse(err))
    } finally {
      this.setLoadingSave(false)
    }
  }

  removeFromMissingImagesList(requestId) {
    this.missingImagesList.splice(
      this.missingImagesList.findIndex((item) => item.id === requestId),
      1
    )
  }

  createParams() {
    let bookingPositions = []
    const createBookingPositions = (articleArray) => {
      for (let article of articleArray) {
        if (this.actionType === 'assets') {
          let filteredPositions = this.scanModus
            ? article.positions
            : article.positions.filter((position) => position.rows)
          for (let position of filteredPositions) {
            //quantity is set to zero
            if (
              (!position.rows || !position.rows.length) &&
              !!position.currentQuantity
            ) {
              bookingPositions.push({
                rfid: null,
                batchKey: null,
                serialKey: null,
                variantId: position.articleVariant
                  ? position.articleVariant.id
                  : null,
                comment: null,
                changeReasonId: null,
                quantity: 0,
                id: article.id,
              })
            } else if (position.rows) {
              for (let row of position.rows) {
                bookingPositions.push({
                  rfid:
                    row.instance && row.instance.rfid
                      ? row.instance.rfid
                      : null,
                  batchKey:
                    row.instance && row.instance.batchKey
                      ? row.instance.batchKey
                      : null,
                  serialKey:
                    row.instance && row.instance.serialKey
                      ? row.instance.serialKey
                      : null,
                  variantId: position.articleVariant
                    ? position.articleVariant.id
                    : null,
                  comment: row.comment,
                  changeReasonId: row.changeReasonId || null,
                  quantity: 1,
                  id: article.id,
                })
              }
            }
          }
        } else {
          let filteredPositions = article.positions.filter(
            (position) => position.rows && position.rows.length
          )
          for (let position of filteredPositions) {
            for (let row of position.rows) {
              bookingPositions.push({
                rfid:
                  row.instance && row.instance.rfid ? row.instance.rfid : null,
                batchKey:
                  row.instance && row.instance.batchKey
                    ? row.instance.batchKey
                    : null,
                serialKey:
                  row.instance && row.instance.serialKey
                    ? row.instance.serialKey
                    : null,
                variantId: position.articleVariant
                  ? position.articleVariant.id
                  : null,
                comment: row.comment,
                changeReasonId: row.changeReasonId || null,
                quantity: position.negativeAmount ? -1 : 1,
                id: article.id,
              })
            }
          }
        }
      }
    }
    for (let contingent of this.contingents) {
      for (let category of contingent.categories) {
        createBookingPositions(category.articles)
      }
    }
    createBookingPositions(this.editCatalogArticles)
    let params = { bookingPositions }

    if (
      this.actionType === 'articleTransfer' ||
      this.actionType === 'assetArticleTransfer'
    ) {
      params.targetBusinesspartnerId = this.targetEmployeeId
    }
    if (this.actionType === 'assets') {
      params.orderMissingAmounts = this.reorderMissingAmounts
      params.automaticReturnAmounts = this.automaticReturnAmounts
      params.neutralizedRFIDs = this.neutralizedRFIDs
    }
    return params
  }
  instanceAlreadyExists(instance) {
    for (let contingent of this.contingents) {
      for (let category of contingent.categories) {
        for (let article of category.articles) {
          for (let position of article.positions) {
            if (
              position.rows &&
              position.rows.length &&
              position.rows.some(
                (row) =>
                  row.instance &&
                  row.instance.rfid === instance.rfid &&
                  row.instance.batchKey === instance.batchKey &&
                  row.instance.serialKey === instance.serialKey
              )
            ) {
              return true
            }
          }
        }
      }
    }
    return false
  }

  getArticlesByArticleId(articleId) {
    let result = []
    for (let contingent of this.contingents) {
      for (let category of contingent.categories) {
        for (let article of category.articles) {
          if (article.article.id === articleId) {
            result.push(article)
          }
        }
      }
    }
    return result
  }
  articleVariantToString(articleId, variant) {
    return `${articleId}_${variant ? variant.id : '_'}`
  }

  async addScannedArticle(scanResponse) {
    let { article, articleVariant, isWithSizes } = scanResponse
    let articleId = article.id
    let variantId = articleVariant.id
    if (isWithSizes && isEmptyGuid(variantId)) {
      throw new Error(i18n.t('inventory:invalidScanResult'))
    }
    let articleInstance = null
    if (!isEmptyGuid(scanResponse.instanceId)) {
      let articleInstances = await this.getInstances(
        articleId,
        isEmptyGuid(variantId) ? null : articleVariant
      )
      articleInstance = articleInstances.find(
        (item) => item.instanceId === scanResponse.instanceId
      )
      let alreadyExists = this.instanceAlreadyExists(articleInstance)
      if (alreadyExists) {
        throw new Error(i18n.t('inventory:alreadySelectedAlert'))
      }
    }
    let articles = this.getArticlesByArticleId(articleId)
    let updatedArticle = articles[0]
    if (!updatedArticle) {
      let newArticle = {
        article: {
          useSerialKeys: scanResponse.useSerialKeys,
          useBatchKeys: scanResponse.useBatchKeys,
          useRFIDs: scanResponse.useRFIDs,
          displayName: article.displayName,
          articleNumber: article.articleNumber,
          id: articleId,
        },
        lastSelectedVariant: null,
        positions: [],
        articleAvailability: {
          id: emptyGuid,
        },
        categoryAvailability: {
          id: emptyGuid,
        },
        contingentAvailability: {
          id: emptyGuid,
        },
        id: `${emptyGuid}_${articleId}`,
      }
      updatedArticle = this.getCurrentArticle(newArticle)
    }
    await this.loadArticleVariants(updatedArticle)
    let positionVariant = updatedArticle.variants.find(
      (variant) => variant.id === variantId
    ) || { id: emptyGuid }
    let foundPosition = updatedArticle.positions.find(
      (position) =>
        (position.articleVariant?.id ?? emptyGuid) ===
        (positionVariant?.id ?? emptyGuid)
    )
    if (!foundPosition) {
      this.addCorrectionVariant(updatedArticle, positionVariant)
      foundPosition = this.getPosition(updatedArticle, positionVariant)
    }
    let oldAmount = foundPosition?.rows?.length ?? 0
    this.updateRows(updatedArticle, positionVariant, oldAmount + 1)
    if (articleInstance) {
      this.updateInstances(
        updatedArticle,
        positionVariant,
        articleInstance,
        oldAmount
      )
    }
  }
  async loadAllInstances(force) {
    if (!force) {
      if (this.instances) {
        return null
      }
    }

    let instances = []
    try {
      this.loadInstancesLoading = true
      instances = await apiClient.getJson(
        APIEndpoints.businessPartner(this.employeeId).instances
      )
      this.instances = instances
      this.neutralizedRFIDs = []
    } catch (e) {
    } finally {
      this.loadInstancesLoading = false
    }
  }

  async getInstances(articleId = null, variant = null) {
    if (this.loadInstancesLoading) {
      await this.app.uiStore.waitUntilLoaded(
        100,
        'app.inventoryStore.loadInstancesLoading'
      )
      return this.getInstances(articleId, variant)
    } else {
      if (articleId && variant) {
        return this.instances.filter(
          (instance) =>
            instance.article &&
            instance.article.id === articleId &&
            instance.variant &&
            instance.variant.id === variant.id
        )
      } else if (articleId) {
        return this.instances.filter(
          (instance) => instance.article && instance.article.id === articleId
        )
      } else {
        return this.instances
      }
    }
  }
  returnReasons = null
  async loadReturnReasons() {
    this.returnReasons = null
    if (this.actionType === 'inventoryBookings') {
      this.returnReasons = await apiClient.getJson(
        APIEndpoints.inventoryBookingReasons
      )
    } else if (this.actionType === 'returnBookings') {
      this.returnReasons = await apiClient.getJson(
        APIEndpoints.returnBookingReasons
      )
    }
  }
  resetActionPage() {
    this.setSubmitSuccess(false)
    this.setSubmitError('')

    this.setReturnResponse({})
    this.setReorderResponse({})
  }
}

export default InventoryStore
